<template>
  <div class="broadcast-layout">
    <Sidebar />

    <div class="main-content">
      <TopHeader />

      <main class="page-content">
        <div class="broadcast-container">
          <!-- Page Header -->
          <div class="page-header">
        <div class="header-content">
          <h1>Broadcast Campaigns</h1>
          <p class="subtitle">Create and manage WhatsApp broadcast campaigns using your approved templates</p>
        </div>
        <button @click="openCreateModal" class="create-campaign-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          Create Campaign
        </button>
      </div>

      <!-- Stats Overview -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon" style="background: rgba(37, 211, 102, 0.1);">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#25D366">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.total_sent.toLocaleString() }}</div>
            <div class="stat-label">Total Sent</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: rgba(59, 130, 246, 0.1);">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#3B82F6">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.delivery_rate }}%</div>
            <div class="stat-label">Delivery Rate</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: rgba(168, 85, 247, 0.1);">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#A855F7">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.active_campaigns }}</div>
            <div class="stat-label">Active Campaigns</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon" style="background: rgba(251, 146, 60, 0.1);">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="#FB923C">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.total_recipients.toLocaleString() }}</div>
            <div class="stat-label">Total Recipients</div>
          </div>
        </div>
      </div>

      <!-- Campaigns Tabs -->
      <div class="campaigns-section">
        <div class="tabs-header">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            @click="activeTab = tab.value"
            :class="['tab-btn', { active: activeTab === tab.value }]"
          >
            {{ tab.label }}
            <span v-if="tab.count > 0" class="tab-count">{{ tab.count }}</span>
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading campaigns...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredCampaigns.length === 0" class="empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <h3>No {{ activeTab }} campaigns</h3>
          <p>{{ getEmptyStateMessage() }}</p>
        </div>

        <!-- Campaigns Table -->
        <div v-else class="campaigns-table-wrapper">
          <table class="campaigns-table">
            <thead>
              <tr>
                <th class="col-campaign">Campaign</th>
                <th class="col-template">Template</th>
                <th class="col-num">Recipients</th>
                <th class="col-num">Sent</th>
                <th class="col-num">Delivered</th>
                <th class="col-num">Read</th>
                <th class="col-num">Failed</th>
                <th>Status</th>
                <th>Created</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="campaign in filteredCampaigns" :key="campaign.id" class="campaign-row">
                <td class="campaign-name-cell">{{ campaign.campaign_name }}</td>
                <td>
                  <div class="template-cell">
                    <span>{{ campaign.template_name }}</span>
                    <span class="lang-chip">{{ campaign.template_language === 'ar' ? 'AR' : 'EN' }}</span>
                  </div>
                </td>
                <td class="num-cell">{{ campaign.total_recipients.toLocaleString() }}</td>
                <td class="num-cell">{{ campaign.sent_count }}</td>
                <td class="num-cell">{{ campaign.delivered_count }}</td>
                <td class="num-cell read-cell">{{ campaign.read_count || 0 }}</td>
                <td class="num-cell failed-cell">{{ campaign.failed_count }}</td>
                <td>
                  <span :class="['status-badge', campaign.status]">
                    {{ campaign.status.replace('_', ' ') }}
                  </span>
                  <div v-if="campaign.status === 'in_progress'" class="inline-progress">
                    <div class="inline-progress-bar">
                      <div class="inline-progress-fill" :style="{ width: campaign.progress_percentage + '%' }"></div>
                    </div>
                    <span class="inline-progress-pct">{{ campaign.progress_percentage }}%</span>
                  </div>
                </td>
                <td class="date-cell">{{ formatDate(campaign.created_at) }}</td>
                <td>
                  <div class="action-btns">
                    <button @click="viewCampaignDetails(campaign)" class="action-btn" title="View Recipients">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
        </div>
      </main>
    </div>

    <!-- Create Campaign Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click="closeCreateModal">
      <div class="modal-container create-campaign-modal" @click.stop>
        <div class="modal-header">
          <h3>Create Broadcast Campaign</h3>
          <button @click="closeCreateModal" class="modal-close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div class="modal-content">
          <!-- Two-Column Layout: Left=Form Fields, Right=Live Preview -->
          <div class="modal-two-column-layout">
            <!-- LEFT COLUMN: All Form Fields -->
            <div class="left-form-column">
              <form @submit.prevent="createCampaign" class="campaign-form">
                <!-- Campaign Name -->
                <div class="form-group">
                  <label for="campaign-name">Campaign Name <span class="required">*</span></label>
                  <input
                    id="campaign-name"
                    v-model="campaignForm.campaign_name"
                    type="text"
                    placeholder="e.g., Summer Sale 2024"
                    class="form-input"
                    required
                  />
                </div>

                <!-- Template Selection -->
                <div class="form-group">
                  <label for="template">WhatsApp Template <span class="required">*</span></label>
                  <select
                    id="template"
                    v-model="campaignForm.template_name"
                    class="form-select"
                    required
                    @change="loadTemplateDetails"
                  >
                    <option value="">Select template</option>
                    <option v-for="template in approvedTemplates" :key="template.name" :value="template.name">
                      {{ template.name }}
                    </option>
                  </select>
                </div>

                <!-- Language Info (Read-only) -->
                <div v-if="campaignForm.template_language" class="form-group">
                  <label>Template Language</label>
                  <div class="info-field">
                    <span>{{ campaignForm.template_language === 'ar' ? 'Arabic (ar)' : campaignForm.template_language === 'en_US' ? 'English (US)' : campaignForm.template_language }}</span>
                  </div>
                </div>

                <!-- Media URL Input (for templates with media headers) -->
                <div v-if="selectedTemplate && hasMediaHeader()" class="form-group">
                  <label>{{ getMediaHeaderType() }} URL <span class="required">*</span></label>
                  <p class="field-hint">Enter the URL for the {{ getMediaHeaderType().toLowerCase() }} that will be sent with this template</p>
                  <input
                    v-model="campaignForm[getMediaUrlField()]"
                    type="url"
                    :placeholder="`https://example.com/${getMediaHeaderType().toLowerCase()}.${getMediaHeaderType() === 'Image' ? 'jpg' : getMediaHeaderType() === 'Video' ? 'mp4' : 'pdf'}`"
                    class="form-input"
                    required
                  />
                </div>

                <!-- Target Audience -->
                <div v-if="selectedTemplate" class="form-group">
                  <label>Target Audience <span class="required">*</span></label>
                  <div class="radio-group">
                    <label class="radio-label">
                      <input type="radio" v-model="campaignForm.target_type" value="all_customers" />
                      <span>All Customers</span>
                    </label>
                    <label class="radio-label">
                      <input type="radio" v-model="campaignForm.target_type" value="by_category" />
                      <span>By Category</span>
                    </label>
                    <label class="radio-label">
                      <input type="radio" v-model="campaignForm.target_type" value="custom_list" />
                      <span>Custom Phone Numbers</span>
                    </label>
                    <label class="radio-label">
                      <input type="radio" v-model="campaignForm.target_type" value="excel_list" />
                      <span>Excel/CSV Upload</span>
                    </label>
                  </div>
                </div>

                <!-- Category Selection (if by_category) -->
                <div v-if="campaignForm.target_type === 'by_category'" class="form-group">
                  <label>Select Categories</label>
                  <div v-if="categories.length === 0" class="field-hint" style="padding: 12px; background: rgba(239, 68, 68, 0.1); border-radius: 8px; color: #EF4444;">
                    No customer categories available. Please create categories in your customer management section first.
                  </div>
                  <div v-else class="checkbox-group">
                    <label v-for="category in categories" :key="category.id" class="checkbox-label">
                      <input
                        type="checkbox"
                        :value="category.id"
                        v-model="campaignForm.category_ids"
                      />
                      <span class="category-name-with-count">
                        <span class="category-name">{{ category.name }}</span>
                        <span class="category-count">{{ category.customer_count }} {{ category.customer_count === 1 ? 'customer' : 'customers' }}</span>
                      </span>
                    </label>
                  </div>
                </div>

                <!-- Custom Phone Numbers (if custom_list) - Customer Search -->
                <div v-if="campaignForm.target_type === 'custom_list'" class="form-group">
                  <label>Select Customers</label>
                  <p class="field-hint">Search and add customers from your database</p>

                  <!-- Search Input -->
                  <div class="customer-search-container">
                    <div class="search-input-wrapper">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="search-icon">
                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                      </svg>
                      <input
                        type="text"
                        v-model="customerSearchQuery"
                        @input="searchCustomers"
                        placeholder="Search by name..."
                        class="customer-search-input"
                      />
                      <div v-if="searchingCustomers" class="search-loading-spinner"></div>
                    </div>

                    <!-- Search Results Dropdown -->
                    <div v-if="customerSearchResults.length > 0 && customerSearchQuery" class="customer-search-results">
                      <div
                        v-for="customer in customerSearchResults"
                        :key="customer.id"
                        @click="addCustomerToList(customer)"
                        class="customer-result-item"
                      >
                        <div class="customer-result-info">
                          <div class="customer-result-name">{{ customer.full_name }}</div>
                          <div class="customer-result-details">
                            <span class="customer-result-phone">{{ customer.phone_number }}</span>
                            <span v-if="customer.category_name" class="customer-result-category">{{ customer.category_name }}</span>
                          </div>
                        </div>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="add-customer-icon">
                          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                        </svg>
                      </div>
                      <div v-if="customerSearchResults.length === 0 && !searchingCustomers" class="no-results">
                        No customers found
                      </div>
                    </div>
                  </div>

                  <!-- Selected Customers Pills -->
                  <div v-if="selectedCustomers.length > 0" class="selected-customers-container">
                    <div class="selected-customers-label">Selected Customers ({{ selectedCustomers.length }})</div>
                    <div class="selected-customers-pills">
                      <div
                        v-for="(customer, index) in selectedCustomers"
                        :key="customer.id || index"
                        class="customer-pill"
                      >
                        <div class="customer-pill-content">
                          <div class="customer-pill-name">{{ customer.full_name }}</div>
                          <div class="customer-pill-details">
                            <span class="customer-pill-phone">{{ customer.phone_number }}</span>
                            <span v-if="customer.category_name" class="customer-pill-category">• {{ customer.category_name }}</span>
                          </div>
                        </div>
                        <button type="button" @click="removeCustomerFromList(index)" class="customer-pill-remove">
                          ×
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Empty State -->
                  <div v-if="selectedCustomers.length === 0" class="empty-customer-list">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" opacity="0.3">
                      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                    </svg>
                    <p>Search and select customers to add them to this campaign</p>
                  </div>
                </div>

                <!-- Excel Upload Section (if excel_list) -->
                <div v-if="campaignForm.target_type === 'excel_list'" class="form-group">
                  <label>Upload Excel/CSV File <span class="required">*</span></label>
                  <p class="field-hint">Upload a spreadsheet with customer data and phone numbers</p>

                  <div class="excel-upload-container">
                    <!-- File Upload Button -->
                    <input
                      type="file"
                      ref="excelFileInput"
                      @change="uploadExcelFile"
                      accept=".csv,.xlsx,.xls"
                      style="display: none"
                    />

                    <button
                      v-if="!excelFile"
                      type="button"
                      @click="$refs.excelFileInput.click()"
                      class="upload-excel-btn"
                      :disabled="uploadingExcel"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                      </svg>
                      {{ uploadingExcel ? 'Uploading...' : 'Upload Excel/CSV File' }}
                    </button>

                    <!-- File Uploaded Indicator -->
                    <div v-if="excelFile" class="file-uploaded-indicator">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                      <span class="file-name">{{ excelFile.name }}</span>
                      <span class="file-rows">{{ excelTotalRows || (excelData ? excelData.length : 0) }} rows</span>
                      <button type="button" @click="clearExcelFile" class="clear-file-btn">×</button>
                    </div>
                  </div>
                </div>

                <!-- Phone Column Selector - HIGHLIGHTED & REQUIRED (Separate Section) -->
                <div v-if="campaignForm.target_type === 'excel_list' && excelColumns.length > 0" class="form-group">
                  <div class="excel-phone-column-highlight">
                    <div class="highlight-banner">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      <span>Required: Select Phone Number Column</span>
                    </div>
                    <label>Phone Number Column <span class="required">*</span></label>
                    <p class="field-hint">Must be in international format — e.g. 201012345678, not 01012345678</p>
                    <select v-model="excelConfig.phone_column" class="excel-column-select" required>
                      <option value="">Select column...</option>
                      <option
                        v-for="col in excelColumns"
                        :key="col.letter"
                        :value="col.letter"
                      >
                        Column {{ col.letter }} - {{ col.label }}
                      </option>
                    </select>

                    <!-- Phone validation warning for selected column -->
                    <div
                      v-if="excelConfig.phone_column && excelPhoneWarnings && excelPhoneWarnings[excelConfig.phone_column] && excelPhoneWarnings[excelConfig.phone_column].invalid > 0"
                      class="excel-phone-warning"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#e67e22">
                        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                      </svg>
                      <div>
                        <strong>{{ excelPhoneWarnings[excelConfig.phone_column].invalid }} number(s) will be skipped</strong> — missing country code or invalid format.
                        <span v-if="excelPhoneWarnings[excelConfig.phone_column].invalid_samples.length">
                          Examples: {{ excelPhoneWarnings[excelConfig.phone_column].invalid_samples.join(', ') }}
                        </span>
                        <br><small>Numbers must be in full international format (e.g. 201012345678, not 01012345678).</small>
                      </div>
                    </div>

                    <div v-if="excelData && excelData.length > 0" class="excel-summary">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                      <span>{{ excelTotalRows || excelData.length }} recipients ready to send</span>
                    </div>
                  </div>
                </div>

                <!-- Name Column Selector - REQUIRED -->
                <div v-if="campaignForm.target_type === 'excel_list' && excelColumns.length > 0" class="form-group">
                  <div class="excel-phone-column-highlight">
                    <div class="highlight-banner">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                      <span>Required: Select Customer Name Column</span>
                    </div>
                    <label>Customer Name Column <span class="required">*</span></label>
                    <p class="field-hint">Used to identify the contact — will be stored as the customer name</p>
                    <select v-model="excelConfig.name_column" class="excel-column-select" required>
                      <option value="">Select column...</option>
                      <option
                        v-for="col in excelColumns"
                        :key="col.letter"
                        :value="col.letter"
                      >
                        Column {{ col.letter }} - {{ col.label }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Variable Mapping (ALWAYS VISIBLE when template selected) -->
                <div v-if="selectedTemplate && getAllTemplateVariables().length > 0" class="form-group">
                  <label>Variable Mapping</label>
                  <p class="field-hint">Map template variables to customer fields or static values</p>

                  <div class="variable-mappings-list">
                    <div
                      v-for="varIndex in getAllTemplateVariables()"
                      :key="varIndex"
                      class="variable-mapping-card"
                    >
                      <div class="mapping-header">
                        <div class="variable-badge">{{ formatVariable(varIndex) }}</div>

                        <!-- Three-way Toggle for Source Type -->
                        <div class="mapping-type-selector">
                          <label
                            class="type-option"
                            :class="{ active: getOrCreateMapping(varIndex).source_type === 'customer_field' }"
                          >
                            <input
                              type="radio"
                              :name="`source-type-${varIndex}`"
                              value="customer_field"
                              v-model="getOrCreateMapping(varIndex).source_type"
                            />
                            <span>Customer Field</span>
                          </label>

                          <label
                            class="type-option"
                            :class="{ active: getOrCreateMapping(varIndex).source_type === 'static_value' }"
                          >
                            <input
                              type="radio"
                              :name="`source-type-${varIndex}`"
                              value="static_value"
                              v-model="getOrCreateMapping(varIndex).source_type"
                            />
                            <span>Static Value</span>
                          </label>

                          <label
                            v-if="campaignForm.target_type === 'excel_list' && excelColumns.length > 0"
                            class="type-option"
                            :class="{ active: getOrCreateMapping(varIndex).source_type === 'excel_column' }"
                          >
                            <input
                              type="radio"
                              :name="`source-type-${varIndex}`"
                              value="excel_column"
                              v-model="getOrCreateMapping(varIndex).source_type"
                            />
                            <span>Excel Column</span>
                          </label>
                        </div>
                      </div>

                      <div class="mapping-input-container">
                        <!-- Customer Field Input -->
                        <template v-if="getOrCreateMapping(varIndex).source_type === 'customer_field'">
                          <select v-model="getOrCreateMapping(varIndex).customer_field" class="mapping-select">
                            <option value="">Select customer field...</option>
                            <option value="full_name">Customer Name</option>
                            <option value="phone_number">Phone Number</option>
                            <option value="email">Email</option>
                            <option value="category_name">Category Name</option>
                            <option value="category_id">Category ID</option>
                            <option value="city">City</option>
                            <option value="country">Country</option>
                            <option value="address">Address</option>
                            <option value="created_at">Registration Date</option>
                            <option value="id">Customer ID</option>
                          </select>
                          <input
                            v-model="getOrCreateMapping(varIndex).default_value"
                            type="text"
                            placeholder="Fallback if field is empty..."
                            class="mapping-input"
                          />
                        </template>

                        <!-- Static Value Input -->
                        <template v-if="getOrCreateMapping(varIndex).source_type === 'static_value'">
                          <input
                            v-model="getOrCreateMapping(varIndex).static_value"
                            type="text"
                            placeholder="Enter static text value..."
                            class="mapping-input full-width"
                          />
                        </template>

                        <!-- Excel Column Selector -->
                        <template v-if="getOrCreateMapping(varIndex).source_type === 'excel_column'">
                          <select v-model="getOrCreateMapping(varIndex).excel_column" class="mapping-select">
                            <option value="">Select Excel column...</option>
                            <option
                              v-for="col in excelColumns"
                              :key="col.letter"
                              :value="col.letter"
                            >
                              Column {{ col.letter }} - {{ col.label }}
                            </option>
                          </select>
                          <input
                            v-model="getOrCreateMapping(varIndex).default_value"
                            type="text"
                            placeholder="Fallback if cell is empty..."
                            class="mapping-input"
                          />
                        </template>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Description -->
                <div class="form-group">
                  <label for="description">Description (Optional)</label>
                  <textarea
                    id="description"
                    v-model="campaignForm.description"
                    class="form-textarea"
                    placeholder="Brief description of this campaign"
                    rows="3"
                  ></textarea>
                </div>

                <!-- Form Actions -->
                <div class="form-actions">
                  <button type="button" @click="closeCreateModal" class="cancel-btn">
                    Cancel
                  </button>
                  <button type="submit" class="submit-btn" :disabled="creating || !isFormValid">
                    <div v-if="creating" class="loading-spinner-small"></div>
                    <span v-else>Create & Send</span>
                  </button>
                </div>

                <!-- Validation Hints (shown when form is invalid) -->
                <div v-if="!isFormValid" class="form-validation-hints">
                  <div class="validation-hint-title">⚠️ Please complete the following:</div>
                  <ul class="validation-hints-list">
                    <li v-if="!campaignForm.campaign_name || campaignForm.campaign_name.trim() === ''">
                      Enter a campaign name
                    </li>
                    <li v-if="!selectedTemplate">
                      Select a WhatsApp template
                    </li>
                    <li v-if="!campaignForm.target_type">
                      Choose a target audience
                    </li>
                    <li v-if="campaignForm.target_type === 'by_category' && (!campaignForm.category_ids || campaignForm.category_ids.length === 0)">
                      Select at least one category
                    </li>
                    <li v-if="campaignForm.target_type === 'custom_list' && selectedCustomers.length === 0">
                      Select at least one customer
                    </li>
                    <li v-if="campaignForm.target_type === 'excel_list' && (!excelFile || !excelData || excelData.length === 0)">
                      Upload an Excel/CSV file
                    </li>
                    <li v-if="campaignForm.target_type === 'excel_list' && excelData && excelData.length > 0 && !excelConfig.phone_column">
                      Select the phone number column
                    </li>
                    <li v-if="campaignForm.target_type === 'excel_list' && excelData && excelData.length > 0 && !excelConfig.name_column">
                      Select the customer name column
                    </li>
                    <li v-if="hasMediaHeader() && (!campaignForm[getMediaUrlField()] || campaignForm[getMediaUrlField()].trim() === '')">
                      Provide {{ getMediaHeaderType() }} URL
                    </li>
                    <li v-if="selectedTemplate && getAllTemplateVariables().length > 0 && !areAllVariablesMapped()">
                      Map all template variables
                    </li>
                  </ul>
                </div>
              </form>
            </div>

            <!-- RIGHT COLUMN: Live Preview (Always Visible, Sticky at Top) -->
            <div v-if="selectedTemplate" class="right-preview-column">
              <div class="sticky-preview-wrapper">
                <!-- Total Recipients Info -->
                <div v-if="totalRecipients > 0" class="recipients-info-card">
                  <div class="recipients-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                    </svg>
                  </div>
                  <div class="recipients-content">
                    <div class="recipients-label">Total Messages to Send</div>
                    <div class="recipients-count">{{ totalRecipients.toLocaleString() }}</div>
                    <div class="recipients-hint">
                      <template v-if="campaignForm.target_type === 'all_customers'">
                        All customers in your database
                      </template>
                      <template v-else-if="campaignForm.target_type === 'by_category'">
                        Customers in selected categories
                      </template>
                      <template v-else-if="campaignForm.target_type === 'custom_list'">
                        Selected customers
                      </template>
                      <template v-else-if="campaignForm.target_type === 'excel_list'">
                        Excel/CSV upload
                      </template>
                    </div>
                  </div>
                </div>

                <div class="preview-phone">
                  <div class="phone-header">
                    <div class="phone-time">9:41</div>
                    <div class="phone-title">WhatsApp</div>
                  </div>
                  <div class="phone-chat">
                    <div class="chat-date">Today</div>
                    <div class="message-bubble">
                      <div v-if="getHeaderComponent()" class="message-header">
                        <div v-if="getHeaderComponent().format === 'TEXT'" class="header-text">
                          {{ replaceVariablesInText(getHeaderComponent().text) }}
                        </div>
                        <!-- Image Header -->
                        <div v-else-if="getHeaderComponent().format === 'IMAGE'" class="header-media">
                          <img
                            v-if="campaignForm.image_url"
                            :src="campaignForm.image_url"
                            alt="Template image"
                            class="preview-image"
                            @error="handleImageError"
                          />
                          <div v-else class="media-placeholder">
                            📷 Image
                          </div>
                        </div>
                        <!-- Video Header -->
                        <div v-else-if="getHeaderComponent().format === 'VIDEO'" class="header-media">
                          <video
                            v-if="campaignForm.video_url"
                            :src="campaignForm.video_url"
                            controls
                            class="preview-video"
                            @error="handleVideoError"
                          >
                            Your browser does not support the video tag.
                          </video>
                          <div v-else class="media-placeholder">
                            🎥 Video
                          </div>
                        </div>
                        <!-- Document Header -->
                        <div v-else-if="getHeaderComponent().format === 'DOCUMENT'" class="header-media">
                          <a
                            v-if="campaignForm.document_url"
                            :href="campaignForm.document_url"
                            target="_blank"
                            class="preview-document"
                          >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                            </svg>
                            <span>{{ getDocumentName() }}</span>
                          </a>
                          <div v-else class="media-placeholder">
                            📄 Document
                          </div>
                        </div>
                      </div>
                      <div class="message-body">
                        {{ replaceVariablesInText(getBodyComponent()?.text || '') }}
                      </div>
                      <div v-if="getFooterComponent()" class="message-footer">
                        {{ getFooterComponent().text }}
                      </div>
                      <div v-if="getButtonsComponent()" class="message-buttons">
                        <div v-for="(button, idx) in getButtonsComponent().buttons" :key="idx" class="wa-button">
                          <span class="button-icon">{{ button.type === 'URL' ? '🔗' : button.type === 'PHONE_NUMBER' ? '📞' : '⚡' }}</span>
                          {{ button.text }}
                        </div>
                      </div>
                      <div class="message-time">9:41 AM</div>
                    </div>
                  </div>
                  <div class="preview-info">
                    <div class="info-icon">💡</div>
                    <div class="info-text">
                      <template v-if="campaignForm.target_type === 'excel_list' && excelData && excelData.length > 0">
                        Showing first row from Excel ({{ excelTotalRows || excelData.length }} total recipients)
                      </template>
                      <template v-else-if="campaignForm.target_type === 'custom_list' && selectedCustomers.length > 0">
                        Previewing: {{ selectedCustomers[0].full_name }} ({{ selectedCustomers.length }} total recipients)
                      </template>
                      <template v-else>
                        Preview updates as you change variable mappings
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Notification Toast -->
    <div v-if="notification" class="notification-toast" :class="notification.type">
      <div class="notification-content">
        <svg v-if="notification.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <div class="notification-text">
          <strong>{{ notification.title }}</strong>
          <p>{{ notification.message }}</p>
        </div>
      </div>
      <button @click="dismissNotification" class="notification-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { AuthService } from '../services/auth'
import { getUserTimezone, formatDateTime } from '../utils/timezone'

export default {
  name: 'WhatsAppBroadcastView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      loading: false,
      creating: false,
      campaigns: [],
      approvedTemplates: [],
      categories: [],
      allCustomers: [],
      activeTab: 'all',
      showCreateModal: false,
      notification: null,
      refreshInterval: null,
      stats: {
        total_sent: 0,
        delivery_rate: 0,
        active_campaigns: 0,
        total_recipients: 0
      },
      campaignForm: {
        campaign_name: '',
        template_name: '',
        template_language: '',
        target_type: 'all_customers',
        category_ids: [],
        custom_phone_numbers: [],
        description: '',
        use_dynamic_variables: false,
        variable_mapping: [],
        image_url: '',
        video_url: '',
        document_url: ''
      },
      phoneNumbersText: '',
      variableMappingCounter: 1,
      tabs: [
        { label: 'All', value: 'all', count: 0 },
        { label: 'Active', value: 'in_progress', count: 0 },
        { label: 'Completed', value: 'completed', count: 0 },
        { label: 'Draft', value: 'draft', count: 0 }
      ],
      // Excel/CSV Upload
      excelFile: null,
      excelData: null,
      excelTotalRows: 0,
      excelColumns: [],
      excelConfig: {
        phone_column: '',
        name_column: '',
        column_headers: {}
      },
      excelPhoneWarnings: null,
      uploadingExcel: false,
      showExcelPreview: false,
      // Customer Search for Custom List
      customerSearchQuery: '',
      customerSearchResults: [],
      searchingCustomers: false,
      selectedCustomers: [],
      searchDebounceTimer: null
    }
  },
  computed: {
    filteredCampaigns () {
      if (this.activeTab === 'all') {
        return this.campaigns
      }
      return this.campaigns.filter(c => c.status === this.activeTab)
    },
    selectedTemplate () {
      if (!this.campaignForm.template_name) return null
      return this.approvedTemplates.find(t => t.name === this.campaignForm.template_name)
    },
    totalRecipients () {
      if (this.campaignForm.target_type === 'all_customers') {
        return this.allCustomers.length
      } else if (this.campaignForm.target_type === 'by_category') {
        return this.categories
          .filter(cat => this.campaignForm.category_ids.includes(cat.id))
          .reduce((sum, cat) => sum + cat.customer_count, 0)
      } else if (this.campaignForm.target_type === 'custom_list') {
        return this.selectedCustomers.length
      } else if (this.campaignForm.target_type === 'excel_list') {
        return this.excelTotalRows || (this.excelData ? this.excelData.length : 0)
      }
      return 0
    },
    isFormValid () {
      // 1. Must have campaign name
      if (!this.campaignForm.campaign_name || this.campaignForm.campaign_name.trim() === '') {
        return false
      }

      // 2. Must have template selected
      if (!this.selectedTemplate) {
        return false
      }

      // 3. Must have target audience selected
      if (!this.campaignForm.target_type) {
        return false
      }

      // 4. Validate target audience specifics
      if (this.campaignForm.target_type === 'by_category') {
        // Must select at least one category
        if (!this.campaignForm.category_ids || this.campaignForm.category_ids.length === 0) {
          return false
        }
      } else if (this.campaignForm.target_type === 'custom_list') {
        // Must have at least one selected customer
        if (this.selectedCustomers.length === 0) {
          return false
        }
      } else if (this.campaignForm.target_type === 'excel_list') {
        // Must have uploaded Excel file
        if (!this.excelFile || !this.excelData || this.excelData.length === 0) {
          return false
        }
        // Must have selected phone column
        if (!this.excelConfig.phone_column) {
          return false
        }
        // Must have selected name column
        if (!this.excelConfig.name_column) {
          return false
        }
      }

      // 5. Check if template has media header and requires media URL
      if (this.hasMediaHeader()) {
        const mediaUrlField = this.getMediaUrlField()
        if (!this.campaignForm[mediaUrlField] || this.campaignForm[mediaUrlField].trim() === '') {
          return false
        }
      }

      // 6. All variables must be mapped
      const templateVariables = this.getAllTemplateVariables()
      if (templateVariables.length > 0) {
        for (const varIndex of templateVariables) {
          const mapping = this.campaignForm.variable_mapping.find(m => m.variable_index === varIndex)
          if (!mapping) {
            return false
          }

          // Determine source type
          const sourceType = mapping.source_type || (mapping.use_static ? 'static_value' : 'customer_field')

          // Check if mapping is complete based on source type
          if (sourceType === 'customer_field') {
            if (!mapping.customer_field) {
              return false
            }
          } else if (sourceType === 'static_value') {
            if (!mapping.static_value || mapping.static_value.trim() === '') {
              return false
            }
          } else if (sourceType === 'excel_column') {
            if (!mapping.excel_column) {
              return false
            }
          }
        }
      }

      // All validations passed
      return true
    }
  },
  async mounted () {
    await this.loadData()
    this.startAutoRefresh()
  },
  beforeUnmount () {
    this.stopAutoRefresh()
  },
  methods: {
    async loadData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadCampaigns(),
          this.loadTemplates(),
          this.loadCategories()
        ])
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        this.loading = false
      }
    },

    async loadCampaigns () {
      try {
        const token = AuthService.getAuthToken()
        const response = await fetch('https://api.quali-d.com/whatsapp/broadcast/campaigns', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          this.campaigns = data.campaigns || []

          // Log in-progress campaigns for debugging
          const inProgressCampaigns = this.campaigns.filter(c => c.status === 'in_progress')
          if (inProgressCampaigns.length > 0) {
            console.log('📊 In-progress campaigns:', inProgressCampaigns.map(c => ({
              id: c.id,
              name: c.campaign_name,
              progress: c.progress_percentage + '%',
              sent: c.sent_count,
              total: c.total_recipients,
              status: c.status
            })))
          }

          this.updateTabCounts()
          this.calculateStats()
        }
      } catch (error) {
        console.error('Error loading campaigns:', error)
      }
    },

    async loadTemplates () {
      try {
        const token = AuthService.getAuthToken()
        const response = await fetch('https://api.quali-d.com/whatsapp/templates', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          // Only show approved templates
          this.approvedTemplates = (data.templates || []).filter(t => t.status === 'APPROVED')
        }
      } catch (error) {
        console.error('Error loading templates:', error)
      }
    },

    async loadCategories () {
      try {
        const token = AuthService.getAuthToken()
        const response = await fetch('https://api.quali-d.com/customers-categories', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        console.log('Categories API Response:', data)

        if (data.success) {
          this.categories = data.categories || []
          console.log('Categories loaded:', this.categories)
          console.log('First category customer_count:', this.categories[0]?.customer_count)
        } else {
          console.error('Categories API returned success=false:', data)
        }
      } catch (error) {
        console.error('Error loading categories:', error)
      }
    },

    async loadAllCustomers () {
      try {
        const token = AuthService.getAuthToken()
        const response = await fetch('https://api.quali-d.com/customers/all', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          this.allCustomers = data.data.customers || []
        }
      } catch (error) {
        console.error('Error loading all customers:', error)
      }
    },

    loadTemplateDetails () {
      const template = this.approvedTemplates.find(t => t.name === this.campaignForm.template_name)
      if (template) {
        this.campaignForm.template_language = template.language
      }
    },

    async createCampaign () {
      try {
        this.creating = true

        // Validate Excel upload if using excel_list
        if (this.campaignForm.target_type === 'excel_list') {
          if (!this.excelData || !this.excelConfig.phone_column) {
            throw new Error('Please upload an Excel file and select the phone number column')
          }
        }

        // Auto-enable dynamic variables if template has variables
        const templateVariables = this.getAllTemplateVariables()
        if (templateVariables.length > 0) {
          this.campaignForm.use_dynamic_variables = true

          // Validation: Check if all template variables have been mapped
          const unmappedVariables = templateVariables.filter(varIndex => {
            const mapping = this.campaignForm.variable_mapping.find(m => m.variable_index === varIndex)
            if (!mapping) return true

            const sourceType = mapping.source_type || (mapping.use_static ? 'static_value' : 'customer_field')

            // Check based on source type
            if (sourceType === 'static_value') {
              return !mapping.static_value || mapping.static_value.trim() === ''
            } else if (sourceType === 'excel_column') {
              return !mapping.excel_column
            } else {
              // customer_field
              return !mapping.customer_field
            }
          })

          if (unmappedVariables.length > 0) {
            throw new Error(`Please map all template variables. Unmapped: ${unmappedVariables.map(v => `{{${v}}}`).join(', ')}`)
          }
        }

        const token = AuthService.getAuthToken()

        // Prepare payload - filter out empty variable mappings
        const payload = { ...this.campaignForm }

        // Prepare Excel data if using excel_list
        if (this.campaignForm.target_type === 'excel_list') {
          const phoneCol = this.excelConfig.phone_column
          const nameCol = this.excelConfig.name_column
          const transformedExcelData = this.excelData.map(row => {
            return {
              ...row,
              phone_number: row[phoneCol],
              full_name: (nameCol && row[nameCol]) ? String(row[nameCol]).trim() : row[phoneCol]
            }
          })

          payload.excel_data = transformedExcelData
          payload.excel_config = this.excelConfig
        }

        // Only include variable mappings that have been actually filled in
        if (payload.variable_mapping && payload.variable_mapping.length > 0) {
          payload.variable_mapping = payload.variable_mapping.filter(mapping => {
            const sourceType = mapping.source_type || (mapping.use_static ? 'static_value' : 'customer_field')

            if (sourceType === 'static_value') {
              return mapping.static_value && mapping.static_value.trim() !== ''
            } else if (sourceType === 'excel_column') {
              return mapping.excel_column
            } else {
              // customer_field
              return mapping.customer_field
            }
          })
        }

        // Create campaign
        const createResponse = await fetch('https://api.quali-d.com/whatsapp/broadcast/campaigns', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        })

        const createData = await createResponse.json()

        if (!createResponse.ok || !createData.success) {
          throw new Error(createData.message || 'Failed to create campaign')
        }

        const campaignId = createData.campaign.id

        this.showNotification({
          type: 'success',
          title: 'Broadcast Started',
          message: `Campaign "${this.campaignForm.campaign_name}" is broadcasting to ${createData.campaign.total_recipients} recipients`
        })

        this.closeCreateModal()
        await this.loadCampaigns()

        // Switch to Active tab to see progress
        this.activeTab = 'in_progress'

        // Start sending campaign (don't wait for completion - run in background)
        fetch(`https://api.quali-d.com/whatsapp/broadcast/campaigns/${campaignId}/send`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        }).then(response => response.json()).then(sendData => {
          console.log('✅ Campaign broadcast finished:', sendData)
          // Refresh campaigns list one final time after completion
          this.loadCampaigns()
        }).catch(error => {
          console.error('❌ Error during campaign broadcast:', error)
          this.showNotification({
            type: 'error',
            title: 'Broadcast Error',
            message: 'An error occurred during broadcasting. Check the campaign status.'
          })
          this.loadCampaigns()
        })
      } catch (error) {
        console.error('Error creating campaign:', error)
        this.showNotification({
          type: 'error',
          title: 'Error',
          message: error.message || 'Failed to create campaign'
        })
      } finally {
        this.creating = false
      }
    },

    async openCreateModal () {
      this.showCreateModal = true
      await this.loadAllCustomers()
    },

    closeCreateModal () {
      this.showCreateModal = false
      this.resetForm()
    },

    resetForm () {
      this.campaignForm = {
        campaign_name: '',
        template_name: '',
        template_language: '',
        target_type: 'all_customers',
        category_ids: [],
        custom_phone_numbers: [],
        description: '',
        use_dynamic_variables: false,
        variable_mapping: []
      }
      this.phoneNumbersText = ''
      this.variableMappingCounter = 1
      this.clearExcelFile()
    },

    async uploadExcelFile (event) {
      const file = event.target.files[0]
      if (!file) return

      this.uploadingExcel = true
      const formData = new FormData()
      formData.append('excel_file', file)

      try {
        const token = AuthService.getAuthToken()
        const response = await fetch('https://api.quali-d.com/whatsapp/broadcast/parse-excel', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        })

        const result = await response.json()

        if (result.success) {
          this.excelFile = file
          this.excelColumns = result.columns
          this.excelData = result.data // full dataset - used for campaign creation
          this.excelTotalRows = result.total_rows
          this.excelPhoneWarnings = result.phone_validation || null
          this.showExcelPreview = true

          // Auto-detect phone column by header keywords
          const phoneCol = this.excelColumns.find(col =>
            col.label.toLowerCase().includes('phone') ||
            col.label.toLowerCase().includes('mobile') ||
            col.label.toLowerCase().includes('number') ||
            col.label.toLowerCase().includes('tel')
          )
          if (phoneCol) {
            this.excelConfig.phone_column = phoneCol.letter
          }

          // Auto-detect name column by header keywords
          const nameCol = this.excelColumns.find(col =>
            col.label.toLowerCase().includes('name') ||
            col.label.toLowerCase().includes('customer') ||
            col.label.toLowerCase().includes('client') ||
            col.label.toLowerCase().includes('contact')
          )
          if (nameCol) {
            this.excelConfig.name_column = nameCol.letter
          }

          this.$toast.success(`Excel parsed: ${result.total_rows} rows found`)
        } else {
          this.$toast.error(result.message || 'Failed to parse Excel file')
        }
      } catch (error) {
        console.error('Excel upload error:', error)
        this.$toast.error('Error uploading Excel file')
      } finally {
        this.uploadingExcel = false
      }
    },

    clearExcelFile () {
      this.excelFile = null
      this.excelData = null
      this.excelTotalRows = 0
      this.excelColumns = []
      this.excelConfig = { phone_column: '', name_column: '', column_headers: {} }
      this.excelPhoneWarnings = null
      this.showExcelPreview = false
      // Clear file input if it exists
      if (this.$refs.excelFileInput) {
        this.$refs.excelFileInput.value = ''
      }
    },

    viewCampaignDetails (campaign) {
      this.$router.push('/whatsapp-broadcast/' + campaign.id)
    },

    calculateCampaignDeliveryRate (campaign) {
      if (!campaign || !campaign.total_recipients || campaign.total_recipients === 0) {
        return 0
      }
      const sent = campaign.sent_count || 0
      return Math.round((sent / campaign.total_recipients) * 100)
    },

    async deleteCampaign (campaign) {
      if (!confirm(`Are you sure you want to delete "${campaign.campaign_name}"?`)) {
        return
      }

      try {
        const token = AuthService.getAuthToken()
        const response = await fetch(`https://api.quali-d.com/whatsapp/broadcast/campaigns/${campaign.id}/cancel`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          this.showNotification({
            type: 'success',
            title: 'Campaign Deleted',
            message: `Campaign "${campaign.campaign_name}" has been deleted`
          })
          await this.loadCampaigns()
        }
      } catch (error) {
        console.error('Error deleting campaign:', error)
        this.showNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to delete campaign'
        })
      }
    },

    updateTabCounts () {
      this.tabs[0].count = this.campaigns.length
      this.tabs[1].count = this.campaigns.filter(c => c.status === 'in_progress').length
      this.tabs[2].count = this.campaigns.filter(c => c.status === 'completed').length
      this.tabs[3].count = this.campaigns.filter(c => c.status === 'draft').length
    },

    calculateStats () {
      const completed = this.campaigns.filter(c => c.status === 'completed')

      this.stats.total_sent = completed.reduce((sum, c) => sum + (c.sent_count || 0), 0)
      this.stats.active_campaigns = this.campaigns.filter(c => c.status === 'in_progress').length
      this.stats.total_recipients = this.campaigns.reduce((sum, c) => sum + (c.total_recipients || 0), 0)

      // Calculate delivery rate: sent_count / total_recipients for completed campaigns
      const completedTotalRecipients = completed.reduce((sum, c) => sum + (c.total_recipients || 0), 0)
      this.stats.delivery_rate = completedTotalRecipients > 0
        ? Math.round((this.stats.total_sent / completedTotalRecipients) * 100)
        : 0
    },

    startAutoRefresh () {
      // Refresh active campaigns every 5 seconds for better UX
      this.refreshInterval = setInterval(() => {
        const hasActiveCampaigns = this.campaigns.some(c => c.status === 'in_progress')
        if (hasActiveCampaigns) {
          console.log('🔄 Auto-refreshing campaigns... Active campaigns found:', hasActiveCampaigns)
          this.loadCampaigns()
        } else {
          console.log('⏸️ No active campaigns, skipping refresh')
        }
      }, 5000) // Changed to 5 seconds for faster updates
    },

    stopAutoRefresh () {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
      }
    },

    formatDate (dateString) {
      return formatDateTime(dateString, getUserTimezone())
    },

    getEmptyStateMessage () {
      const messages = {
        all: 'Create your first campaign to get started',
        in_progress: 'No campaigns are currently running',
        completed: 'No completed campaigns yet',
        draft: 'No draft campaigns'
      }
      return messages[this.activeTab]
    },

    showNotification (notification) {
      this.notification = notification
      setTimeout(() => {
        this.notification = null
      }, 5000)
    },

    dismissNotification () {
      this.notification = null
    },

    // Dynamic Variable Mapping Methods
    hasVariables (text) {
      if (!text) return false
      return /\{\{(\d+)\}\}/.test(text)
    },

    extractVariables (text) {
      if (!text) return []
      const matches = text.match(/\{\{(\d+)\}\}/g)
      if (!matches) return []
      return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))]
    },

    getOrCreateMapping (varIndex) {
      let mapping = this.campaignForm.variable_mapping.find(m => m.variable_index === varIndex)
      if (!mapping) {
        mapping = {
          variable_index: varIndex,
          source_type: 'customer_field', // NEW: default source type
          customer_field: '',
          static_value: '',
          excel_column: '', // NEW
          default_value: '',
          use_static: false // Keep for backwards compatibility
        }
        this.campaignForm.variable_mapping.push(mapping)
      }
      // Ensure source_type exists (for backwards compatibility)
      if (!mapping.source_type) {
        mapping.source_type = mapping.use_static ? 'static_value' : 'customer_field'
      }
      return mapping
    },

    addVariableMapping () {
      this.campaignForm.variable_mapping.push({
        variable_index: String(this.variableMappingCounter),
        customer_field: '',
        default_value: ''
      })
      this.variableMappingCounter++
    },

    removeVariableMapping (index) {
      this.campaignForm.variable_mapping.splice(index, 1)
    },

    formatVariable (varIndex) {
      return `{{${varIndex}}}`
    },

    getFieldExample (fieldName) {
      const examples = {
        full_name: 'Ahmed Mohamed',
        phone_number: '+201234567890',
        email: 'ahmed@example.com',
        category_name: 'VIP Customer',
        category_id: '5',
        city: 'Cairo',
        country: 'Egypt',
        address: '123 Main St',
        created_at: '2025-01-15',
        id: '123'
      }
      return examples[fieldName] || fieldName
    },

    getHeaderComponent () {
      if (!this.selectedTemplate) return null
      return this.selectedTemplate.components.find(c => c.type === 'HEADER')
    },

    getBodyComponent () {
      if (!this.selectedTemplate) return null
      return this.selectedTemplate.components.find(c => c.type === 'BODY')
    },

    getFooterComponent () {
      if (!this.selectedTemplate) return null
      return this.selectedTemplate.components.find(c => c.type === 'FOOTER')
    },

    getButtonsComponent () {
      if (!this.selectedTemplate) return null
      return this.selectedTemplate.components.find(c => c.type === 'BUTTONS')
    },

    replaceVariablesInText (text) {
      if (!text) return ''
      let result = text

      // Replace variables with mapped values
      this.campaignForm.variable_mapping.forEach(mapping => {
        const variable = `{{${mapping.variable_index}}}`
        let value = ''

        // Determine source type (backwards compatible)
        const sourceType = mapping.source_type || (mapping.use_static ? 'static_value' : 'customer_field')

        if (sourceType === 'static_value') {
          // Use static value directly
          value = mapping.static_value || variable
        } else if (sourceType === 'excel_column') {
          // Use first row value from Excel column
          value = this.getExcelColumnPreview(mapping.excel_column) || mapping.default_value || variable
        } else if (sourceType === 'customer_field') {
          // Use customer field - check if we have actual customer data first
          if (this.campaignForm.target_type === 'custom_list' && this.selectedCustomers.length > 0) {
            // Use first selected customer's actual data
            value = this.getCustomerFieldValue(this.selectedCustomers[0], mapping.customer_field) || mapping.default_value || variable
          } else {
            // Use example value
            value = mapping.customer_field ? this.getFieldExample(mapping.customer_field) : (mapping.default_value || variable)
          }
        } else {
          // No mapping - show variable placeholder
          value = variable
        }

        result = result.replace(new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g'), value)
      })

      return result
    },

    getExcelColumnPreview (columnLetter) {
      if (!columnLetter || !this.excelData || this.excelData.length === 0) {
        return null
      }

      // Get first row of data
      const firstRow = this.excelData[0]
      if (!firstRow || !firstRow[columnLetter]) {
        return null
      }

      return firstRow[columnLetter]
    },

    getCustomerFieldValue (customer, fieldName) {
      if (!customer || !fieldName) {
        return null
      }

      // Special handling for created_at date formatting
      if (fieldName === 'created_at' && customer[fieldName]) {
        const date = new Date(customer[fieldName])
        return date.toLocaleDateString()
      }

      // Return the field value from the customer object
      return customer[fieldName] || null
    },

    generatePreviewText (customerName, customerCategory) {
      if (!this.selectedTemplate) return ''

      const bodyComponent = this.selectedTemplate.components.find(c => c.type === 'BODY')
      if (!bodyComponent || !bodyComponent.text) return ''

      return this.replaceVariablesInText(bodyComponent.text)
    },

    areAllVariablesMapped () {
      const templateVariables = this.getAllTemplateVariables()
      if (templateVariables.length === 0) {
        return true
      }

      for (const varIndex of templateVariables) {
        const mapping = this.campaignForm.variable_mapping.find(m => m.variable_index === varIndex)
        if (!mapping) {
          return false
        }

        const sourceType = mapping.source_type || (mapping.use_static ? 'static_value' : 'customer_field')

        if (sourceType === 'customer_field' && !mapping.customer_field) {
          return false
        } else if (sourceType === 'static_value' && (!mapping.static_value || mapping.static_value.trim() === '')) {
          return false
        } else if (sourceType === 'excel_column' && !mapping.excel_column) {
          return false
        }
      }

      return true
    },

    getAllTemplateVariables () {
      if (!this.selectedTemplate) return []
      const allVariables = []

      this.selectedTemplate.components.forEach(component => {
        // HEADER component
        if (component.type === 'HEADER' && component.text) {
          const variables = this.extractVariables(component.text)
          variables.forEach(v => {
            allVariables.push('header')
          })
        }

        // BODY component
        if (component.type === 'BODY') {
          if (component.text) {
            const variables = this.extractVariables(component.text)
            allVariables.push(...variables)
          }
          // Also check example.body_text
          if (component.example?.body_text && Array.isArray(component.example.body_text)) {
            for (let i = 0; i < component.example.body_text.length; i++) {
              allVariables.push(String(i + 1))
            }
          }
        }

        // BUTTONS component - variables in button URLs need "button_" prefix
        if (component.type === 'BUTTONS' && component.buttons && Array.isArray(component.buttons)) {
          component.buttons.forEach((button, buttonIndex) => {
            if (button.url) {
              const urlVariables = this.extractVariables(button.url)
              urlVariables.forEach(v => {
                allVariables.push(`button_${v}`)
              })
            }
            if (button.text) {
              const textVariables = this.extractVariables(button.text)
              textVariables.forEach(v => {
                allVariables.push(`button_${v}`)
              })
            }
          })
        }
      })

      // Return unique variables sorted
      const unique = [...new Set(allVariables)]
      return unique.sort((a, b) => {
        // Sort: header first, then numbers, then button_numbers
        if (a === 'header') return -1
        if (b === 'header') return 1
        const aNum = parseInt(a.replace('button_', ''))
        const bNum = parseInt(b.replace('button_', ''))
        return aNum - bNum
      })
    },

    // Phone Number Management Methods
    // Customer Search Methods
    searchCustomers () {
      // Debounce search
      clearTimeout(this.searchDebounceTimer)

      if (!this.customerSearchQuery || this.customerSearchQuery.trim().length < 2) {
        this.customerSearchResults = []
        return
      }

      this.searchDebounceTimer = setTimeout(async () => {
        this.searchingCustomers = true
        try {
          const token = AuthService.getAuthToken()
          const response = await fetch(`https://api.quali-d.com/customers?page=1&per_page=15&search_name=${encodeURIComponent(this.customerSearchQuery)}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json'
            }
          })

          const data = await response.json()

          if (data.success && data.data && data.data.customers) {
            // Filter out already selected customers
            this.customerSearchResults = data.data.customers.filter(customer =>
              !this.selectedCustomers.some(selected => selected.id === customer.id)
            )
          } else {
            this.customerSearchResults = []
          }
        } catch (error) {
          console.error('Error searching customers:', error)
          this.$toast.error('Error searching customers')
        } finally {
          this.searchingCustomers = false
        }
      }, 300)
    },

    addCustomerToList (customer) {
      // Check if already added
      if (this.selectedCustomers.some(c => c.id === customer.id)) {
        this.$toast.warning('Customer already added')
        return
      }

      this.selectedCustomers.push(customer)
      this.customerSearchQuery = ''
      this.customerSearchResults = []

      // Update campaignForm.custom_phone_numbers for backend compatibility
      this.updateCustomPhoneNumbers()

      this.$toast.success(`${customer.full_name} added`)
    },

    removeCustomerFromList (index) {
      const customer = this.selectedCustomers[index]
      this.selectedCustomers.splice(index, 1)

      // Update campaignForm.custom_phone_numbers for backend compatibility
      this.updateCustomPhoneNumbers()

      this.$toast.success(`${customer.full_name} removed`)
    },

    updateCustomPhoneNumbers () {
      // Sync selectedCustomers with campaignForm.custom_phone_numbers
      this.campaignForm.custom_phone_numbers = this.selectedCustomers.map(c => c.phone_number)
    },

    // Media Header Methods
    hasMediaHeader () {
      const header = this.getHeaderComponent()
      return header && header.format && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.format)
    },

    getMediaHeaderType () {
      const header = this.getHeaderComponent()
      if (!header || !header.format) return ''
      return header.format.charAt(0) + header.format.slice(1).toLowerCase() // IMAGE -> Image
    },

    getMediaUrlField () {
      const header = this.getHeaderComponent()
      if (!header || !header.format) return ''
      return header.format.toLowerCase() + '_url' // IMAGE -> image_url
    },

    getDocumentName () {
      if (!this.campaignForm.document_url) return 'Document'
      const url = this.campaignForm.document_url
      const parts = url.split('/')
      const filename = parts[parts.length - 1]
      return filename || 'Document'
    },

    handleImageError (event) {
      event.target.style.display = 'none'
      event.target.parentElement.innerHTML = '<div class="media-placeholder media-error">📷 Invalid image URL</div>'
    },

    handleVideoError (event) {
      event.target.style.display = 'none'
      event.target.parentElement.innerHTML = '<div class="media-placeholder media-error">🎥 Invalid video URL</div>'
    }
  }
}
</script>

<style scoped>
.broadcast-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary);
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

.broadcast-container {
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.create-campaign-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #25D366;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-campaign-btn:hover {
  background: #20c05a;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: var(--text-secondary);
  margin-top: 4px;
}

/* Campaigns Section */
.campaigns-section {
  background: var(--bg-primary);
  border-radius: 12px;
  overflow: hidden;
}

.tabs-header {
  display: flex;
  border-bottom: 1px solid var(--border-primary);
  padding: 0 20px;
  gap: 8px;
}

.tab-btn {
  padding: 16px 20px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-btn.active {
  color: #25D366;
  border-bottom-color: #25D366;
}

.tab-count {
  padding: 2px 8px;
  background: var(--bg-tertiary);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.tab-btn.active .tab-count {
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #25D366;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state svg {
  color: var(--text-tertiary);
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 18px;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

/* ── Campaigns Table ─────────────────────────────────────── */
.campaigns-table-wrapper {
  overflow-x: auto;
}

.campaigns-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13.5px;
}

/* Header */
.campaigns-table thead tr {
  background: var(--bg-tertiary);
}

.campaigns-table thead th {
  padding: 12px 18px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  white-space: nowrap;
  border-top: 1px solid var(--border-primary);
  border-bottom: 2px solid var(--border-primary);
}

.campaigns-table thead th:first-child {
  border-left: 1px solid var(--border-primary);
  border-radius: 10px 0 0 0;
}

.campaigns-table thead th:last-child {
  border-right: 1px solid var(--border-primary);
  border-radius: 0 10px 0 0;
}

.campaigns-table thead th.col-num    { text-align: right; }
.campaigns-table thead th.col-actions { text-align: center; }

/* Vertical separators — header */
.campaigns-table thead th + th {
  border-left: 1px solid var(--border-primary);
}

/* Body rows */
.campaign-row td {
  padding: 13px 18px;
  border-bottom: 1px solid var(--border-primary);
  color: var(--text-primary);
  vertical-align: middle;
  background: var(--bg-primary);
  transition: background 0.15s ease, color 0.15s ease;
}

.campaign-row td:first-child {
  border-left: 3px solid transparent;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.campaign-row td:last-child {
  border-right: 1px solid var(--border-primary);
}

.campaign-row:last-child td:first-child  { border-radius: 0 0 0 10px; }
.campaign-row:last-child td:last-child   { border-radius: 0 0 10px 0; }
.campaign-row:last-child td              { border-bottom: 1px solid var(--border-primary); }

/* Vertical separators — body */
.campaign-row td + td {
  border-left: 1px solid var(--border-primary);
}

/* Hover */
.campaign-row:hover td {
  background: rgba(37, 211, 102, 0.04);
}

.campaign-row:hover td:first-child {
  border-left-color: #25D366;
}

/* Campaign name */
.campaign-name-cell {
  font-weight: 600;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 160px;
}

/* Template */
.template-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  min-width: 140px;
}

.lang-chip {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 4px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  letter-spacing: 0.04em;
  border: 1px solid var(--border-primary);
  flex-shrink: 0;
}

/* Numeric cells */
.num-cell {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
}

.read-cell   { color: #8B5CF6; font-weight: 700; }
.failed-cell { color: #EF4444; font-weight: 600; }

/* Date */
.date-cell {
  white-space: nowrap;
  color: var(--text-secondary);
  font-size: 12px;
}

/* Actions */
.col-actions {
  text-align: center;
  white-space: nowrap;
}

.action-btns {
  display: flex;
  gap: 6px;
  justify-content: center;
}

/* Inline progress (in_progress rows) */
.inline-progress {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.inline-progress-bar {
  flex: 1;
  height: 4px;
  background: var(--bg-tertiary);
  border-radius: 2px;
  overflow: hidden;
  min-width: 60px;
}

.inline-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #25D366, #20c05a);
  border-radius: 2px;
  transition: width 0.4s ease;
}

.inline-progress-pct {
  font-size: 11px;
  font-weight: 600;
  color: #25D366;
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.draft {
  background: rgba(107, 114, 128, 0.1);
  color: #6B7280;
}

.status-badge.in_progress {
  background: rgba(59, 130, 246, 0.1);
  color: #3B82F6;
}

.status-badge.completed {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.05));
  color: #22C55E;
  border: 1px solid rgba(34, 197, 94, 0.3);
  font-weight: 700;
}

.status-badge.failed {
  background: rgba(239, 68, 68, 0.1);
  color: #EF4444;
}

.action-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.delete-btn:hover {
  border-color: #EF4444;
  color: #EF4444;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: var(--bg-secondary);
  border-radius: 16px;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  border: none;
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close-btn:hover {
  background: var(--bg-primary);
  color: var(--text-primary);
}

.modal-content {
  padding: 24px;
  overflow-y: auto;
}

.campaign-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
}

.required {
  color: #EF4444;
}

.form-input,
.form-select,
.form-textarea {
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  font-family: inherit;
  transition: all 0.2s ease;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.form-textarea {
  resize: vertical;
  font-family: inherit;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.radio-group,
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-label,
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.radio-label:hover,
.checkbox-label:hover {
  border-color: #25D366;
  background: rgba(37, 211, 102, 0.02);
}

.radio-label input,
.checkbox-label input {
  cursor: pointer;
}

.field-hint {
  font-size: 12px;
  color: var(--text-tertiary);
  margin: 0;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid var(--border-primary);
}

.cancel-btn,
.submit-btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: transparent;
  border: 1px solid var(--border-primary);
  color: var(--text-secondary);
}

.cancel-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.submit-btn {
  background: #25D366;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
}

.submit-btn:hover:not(:disabled) {
  background: #20c05a;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  background: #666;
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Form Validation Hints */
.form-validation-hints {
  margin-top: 20px;
  padding: 16px;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
}

.validation-hint-title {
  font-size: 14px;
  font-weight: 600;
  color: #EF4444;
  margin-bottom: 12px;
}

.validation-hints-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.validation-hints-list li {
  font-size: 13px;
  color: var(--text-secondary);
  padding: 6px 0;
  padding-left: 20px;
  position: relative;
}

.validation-hints-list li::before {
  content: '•';
  position: absolute;
  left: 8px;
  color: #EF4444;
  font-weight: bold;
}

/* Notification */
.notification-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  min-width: 320px;
  max-width: 480px;
  padding: 16px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.notification-toast.success {
  border-left: 4px solid #22C55E;
}

.notification-toast.error {
  border-left: 4px solid #EF4444;
}

.notification-content {
  display: flex;
  gap: 12px;
  flex: 1;
}

.notification-content svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.notification-toast.success svg {
  color: #22C55E;
}

.notification-toast.error svg {
  color: #EF4444;
}

.notification-text strong {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.notification-text p {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
}

.notification-close {
  background: var(--bg-tertiary);
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.notification-close:hover {
  background: var(--bg-primary);
  color: var(--text-primary);
}

/* Dynamic Personalization Toggle */
.personalization-toggle {
  background: linear-gradient(135deg, rgba(37, 211, 102, 0.05) 0%, rgba(37, 211, 102, 0.02) 100%);
  border: 1px solid rgba(37, 211, 102, 0.2);
  border-radius: 12px;
  padding: 16px;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  margin: 0;
}

.toggle-checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch {
  position: relative;
  width: 48px;
  height: 26px;
  background: var(--border-primary);
  border-radius: 13px;
  transition: background 0.3s ease;
  flex-shrink: 0;
}

.toggle-switch::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  top: 3px;
  left: 3px;
  transition: transform 0.3s ease;
}

.toggle-checkbox:checked + .toggle-switch {
  background: #25D366;
}

.toggle-checkbox:checked + .toggle-switch::after {
  transform: translateX(22px);
}

.toggle-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toggle-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.toggle-description {
  font-size: 12px;
  color: var(--text-secondary);
}

/* Variable Mapping Section */
.variable-mapping-section {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  margin-top: -8px;
}

.mapping-header h4 {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.mapping-header p {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 20px 0;
}

/* Split View Layout */
.mapping-split-view {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 20px;
  min-height: 500px;
}

.mappings-panel {
  display: flex;
  flex-direction: column;
}

.mappings-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.variable-mapping-row {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 16px;
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 16px;
  align-items: center;
  transition: all 0.2s ease;
}

.variable-mapping-row:hover {
  border-color: rgba(37, 211, 102, 0.3);
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.1);
}

.variable-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  min-width: 80px;
}

.variable-badge-large {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  border: 2px solid rgba(37, 211, 102, 0.3);
}

.variable-description {
  font-size: 11px;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.mapping-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
}

.mapping-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  flex: 1;
}

.control-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.control-group label {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
}

.field-select,
.default-input {
  padding: 10px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 13px;
  transition: all 0.2s ease;
}

.field-select {
  cursor: pointer;
}

.field-select:hover {
  border-color: var(--border-secondary);
}

.field-select:focus,
.default-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.default-input::placeholder {
  color: var(--text-tertiary);
  font-style: italic;
}

.mapping-example {
  grid-column: 1 / -1;
  margin-top: 8px;
  padding: 10px;
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 6px;
  font-size: 12px;
  display: flex;
  gap: 6px;
}

.example-label {
  font-weight: 600;
  color: #3B82F6;
}

.example-value {
  color: var(--text-primary);
  font-weight: 500;
}

.template-mapping-panel,
.preview-panel {
  display: flex;
  flex-direction: column;
}

.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--border-primary);
}

/* WhatsApp Template Display */
.whatsapp-template-display {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}

.template-component {
  margin-bottom: 16px;
}

.template-component:last-child {
  margin-bottom: 0;
}

.component-section {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  overflow: hidden;
}

.component-label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-primary);
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.header-section .component-label {
  background: rgba(59, 130, 246, 0.05);
  color: #3B82F6;
}

.body-section .component-label {
  background: rgba(37, 211, 102, 0.05);
  color: #25D366;
}

.footer-section .component-label {
  background: rgba(107, 114, 128, 0.05);
  color: #6B7280;
}

.buttons-section .component-label {
  background: rgba(168, 85, 247, 0.05);
  color: #A855F7;
}

.component-content {
  padding: 14px;
}

.template-text {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-primary);
  margin-bottom: 12px;
  white-space: pre-wrap;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.footer-text {
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 0;
}

.media-header {
  text-align: center;
  padding: 20px;
}

.media-indicator {
  display: inline-block;
  padding: 8px 16px;
  background: var(--bg-tertiary);
  border-radius: 6px;
  font-size: 13px;
  color: var(--text-secondary);
}

.button-display {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 13px;
}

.button-display:last-child {
  margin-bottom: 0;
}

.button-icon {
  font-size: 16px;
}

.button-text {
  font-weight: 500;
  color: var(--text-primary);
}

.button-type {
  margin-left: auto;
  font-size: 11px;
  color: var(--text-tertiary);
  text-transform: uppercase;
}

/* Variable Mappings Inline */
.variable-mappings-inline {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.variable-mapper {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(37, 211, 102, 0.03);
  border: 1px solid rgba(37, 211, 102, 0.2);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.variable-mapper:hover {
  background: rgba(37, 211, 102, 0.05);
  border-color: rgba(37, 211, 102, 0.3);
}

.variable-name {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  min-width: 60px;
  justify-content: center;
}

.arrow-icon {
  flex-shrink: 0;
  color: var(--text-tertiary);
}

.inline-select {
  flex: 1;
  min-width: 180px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.inline-select:hover {
  border-color: var(--border-secondary);
}

.inline-select:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.inline-default-input {
  flex: 0.8;
  min-width: 120px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  transition: all 0.2s ease;
}

.inline-default-input::placeholder {
  color: var(--text-tertiary);
  font-style: italic;
}

.inline-default-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

/* WhatsApp Phone Preview */
.preview-panel {
  position: sticky;
  top: 100px;
  align-self: flex-start;
}

.preview-phone {
  background: #E5DDD5;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border: 1px solid var(--border-primary);
}

.phone-header {
  background: #075E54;
  color: white;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.phone-time {
  font-size: 12px;
  font-weight: 500;
}

.phone-title {
  font-size: 14px;
  font-weight: 600;
}

.phone-chat {
  padding: 16px;
  min-height: 400px;
  max-height: 600px;
  overflow-y: auto;
  background-image:
    repeating-linear-gradient(
      45deg,
      transparent,
      transparent 10px,
      rgba(0, 0, 0, 0.01) 10px,
      rgba(0, 0, 0, 0.01) 20px
    );
}

.chat-date {
  text-align: center;
  font-size: 12px;
  color: #667781;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 12px;
  border-radius: 12px;
  display: inline-block;
  margin: 0 auto 16px;
  width: fit-content;
  display: block;
}

.message-bubble {
  background: white;
  border-radius: 8px;
  padding: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  max-width: 100%;
  position: relative;
  margin-left: 0;
  border-top-left-radius: 0;
  overflow: hidden;
}

.message-header {
  margin-bottom: 0;
}

.header-text {
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin-bottom: 4px;
  padding: 8px 12px 4px 12px;
}

.header-media {
  background: #F0F0F0;
  padding: 0;
  text-align: center;
  font-size: 13px;
  color: #667781;
  margin-bottom: 0;
}

.message-body {
  font-size: 14px;
  line-height: 1.5;
  color: #000;
  white-space: pre-wrap;
  word-wrap: break-word;
  padding: 8px 12px;
  margin-bottom: 0;
}

.message-footer {
  font-size: 12px;
  color: #667781;
  padding: 0 12px 8px 12px;
  margin-top: 0;
  border-top: 1px solid #E9EDEF;
}

.message-buttons {
  margin: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.wa-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  background: white;
  border: 1px solid #D1D7DB;
  border-radius: 6px;
  color: #00A5F4;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.wa-button:hover {
  background: #F7F8FA;
}

.message-time {
  font-size: 11px;
  color: #667781;
  padding: 0 12px 8px 12px;
  text-align: right;
  margin-top: 4px;
}

.preview-info {
  background: rgba(37, 211, 102, 0.1);
  border-top: 1px solid rgba(37, 211, 102, 0.2);
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.info-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.info-text {
  font-size: 12px;
  line-height: 1.4;
  color: #075E54;
}

/* Live Preview Section */
.live-preview-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-primary);
}

.preview-header h5 {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.preview-header p {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
}

.preview-examples {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-example {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-customer-info {
  font-size: 12px;
  color: var(--text-secondary);
}

.whatsapp-bubble {
  background: #DCF8C6;
  color: #000;
  padding: 12px 14px;
  border-radius: 8px;
  border-top-left-radius: 0;
  font-size: 14px;
  line-height: 1.5;
  max-width: 100%;
  word-wrap: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Two-Column Modal Layout */
.modal-two-column-layout {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 24px;
  min-height: 500px;
}

.left-form-column {
  overflow-y: auto;
  max-height: calc(90vh - 120px);
  padding-right: 12px;
}

.right-preview-column {
  display: flex;
  flex-direction: column;
}

.sticky-preview-wrapper {
  position: sticky;
  top: 0;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Read-only Info Field */
.info-field {
  padding: 12px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
}

/* Variable Mappings List */
.variable-mappings-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Variable Mapping Card */
.variable-mapping-card {
  padding: 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.variable-mapping-card:hover {
  border-color: rgba(37, 211, 102, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.mapping-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
}

.mapping-type-toggle {
  display: flex;
  background: var(--bg-secondary);
  border-radius: 6px;
  padding: 2px;
  gap: 2px;
}

.toggle-option {
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
}

.toggle-option input[type="radio"] {
  display: none;
}

.toggle-option.active {
  background: #25D366;
  color: white;
}

.mapping-input-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.mapping-select {
  flex: 1;
  min-width: 180px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mapping-select:hover {
  border-color: var(--border-secondary);
}

.mapping-select:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.mapping-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  transition: all 0.2s ease;
}

.mapping-input.full-width {
  width: 100%;
}

.mapping-input::placeholder {
  color: var(--text-tertiary);
  font-style: italic;
}

.mapping-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.compact-variable-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.compact-variable-row:hover {
  border-color: rgba(37, 211, 102, 0.3);
  background: rgba(37, 211, 102, 0.02);
}

.variable-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  min-width: 60px;
  border: 2px solid rgba(37, 211, 102, 0.3);
}

.compact-variable-row .arrow-icon {
  flex-shrink: 0;
  color: var(--text-tertiary);
}

.compact-field-select {
  flex: 1;
  min-width: 180px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.compact-field-select:hover {
  border-color: var(--border-secondary);
}

.compact-field-select:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.compact-default-input {
  flex: 0.8;
  min-width: 120px;
  padding: 8px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 13px;
  transition: all 0.2s ease;
}

.compact-default-input::placeholder {
  color: var(--text-tertiary);
  font-style: italic;
}

.compact-default-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

/* Recipients Info Card */
.recipients-info-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
}

.recipients-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(99, 102, 241, 0.12);
  border-radius: 12px;
  color: #6366F1;
  flex-shrink: 0;
}

.recipients-content {
  flex: 1;
}

.recipients-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 4px;
}

.recipients-count {
  font-size: 28px;
  font-weight: 700;
  color: #6366F1;
  line-height: 1;
  margin-bottom: 4px;
}

.recipients-hint {
  font-size: 12px;
  color: var(--text-tertiary);
  font-style: italic;
}

/* Phone Numbers List */
.phone-numbers-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.phone-number-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.phone-input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  font-family: 'Courier New', monospace;
  transition: all 0.2s ease;
}

.phone-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

/* Customer Search Styles */
.customer-search-container {
  position: relative;
  margin-bottom: 20px;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 14px;
  color: var(--text-secondary);
  pointer-events: none;
}

.customer-search-input {
  width: 100%;
  padding: 12px 14px 12px 44px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s ease;
}

.customer-search-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.search-loading-spinner {
  position: absolute;
  right: 14px;
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-primary);
  border-top-color: #25D366;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.customer-search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 8px;
  max-height: 300px;
  overflow-y: auto;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
}

.customer-result-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  border-bottom: 1px solid var(--border-primary);
}

.customer-result-item:last-child {
  border-bottom: none;
}

.customer-result-item:hover {
  background: rgba(37, 211, 102, 0.05);
}

.customer-result-info {
  flex: 1;
}

.customer-result-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.customer-result-details {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-secondary);
}

.customer-result-phone {
  font-family: monospace;
}

.customer-result-category {
  padding: 2px 8px;
  background: rgba(37, 211, 102, 0.1);
  border-radius: 4px;
  color: #25D366;
  font-weight: 500;
}

.add-customer-icon {
  color: #25D366;
  flex-shrink: 0;
}

.no-results {
  padding: 20px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
}

/* Selected Customers Pills */
.selected-customers-container {
  margin-top: 20px;
}

.selected-customers-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.selected-customers-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.customer-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.customer-pill:hover {
  border-color: #25D366;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.1);
}

.customer-pill-content {
  flex: 1;
}

.customer-pill-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 3px;
}

.customer-pill-details {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-secondary);
}

.customer-pill-phone {
  font-family: monospace;
}

.customer-pill-category {
  color: #25D366;
  font-weight: 500;
}

.customer-pill-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(239, 68, 68, 0.1);
  color: #EF4444;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.customer-pill-remove:hover {
  background: #EF4444;
  color: white;
}

/* Empty State */
.empty-customer-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  background: var(--bg-tertiary);
  border: 2px dashed var(--border-primary);
  border-radius: 12px;
  color: var(--text-secondary);
}

.empty-customer-list p {
  margin-top: 12px;
  font-size: 14px;
}

.remove-phone-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: #EF4444;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.remove-phone-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #EF4444;
}

.add-phone-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: 1px dashed var(--border-primary);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.add-phone-btn:hover {
  background: rgba(37, 211, 102, 0.05);
  border-color: #25D366;
  color: #25D366;
  border-style: solid;
}

/* Category Count Display */
.category-name-with-count {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  gap: 12px;
}

.category-name {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.category-count {
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.checkbox-label:hover .category-count {
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }

  .modal-two-column-layout {
    grid-template-columns: 1fr;
  }

  .right-preview-column {
    order: -1;
  }

  .sticky-preview-wrapper {
    position: relative;
  }

  .left-form-column {
    max-height: none;
    padding-right: 0;
  }

  .compact-variable-row {
    flex-direction: column;
    align-items: stretch;
  }

  .compact-field-select,
  .compact-default-input {
    min-width: 100%;
  }

  .page-content {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    gap: 16px;
  }

  .create-campaign-btn {
    width: 100%;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .campaigns-table-wrapper {
    padding: 0 8px 16px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .notification-toast {
    left: 16px;
    right: 16px;
    min-width: auto;
  }

  .mapping-row {
    grid-template-columns: 1fr;
  }

  .variable-mapping-section {
    padding: 16px;
  }

  .mapping-split-view {
    grid-template-columns: 1fr;
  }

  .preview-panel {
    position: relative;
    top: 0;
  }

  .phone-chat {
    max-height: 400px;
  }
}

/* Media Preview Styles */
.message-header .header-media {
  margin: 0;
  border-radius: 0;
  overflow: hidden;
  border-top-right-radius: 8px;
}

.preview-image {
  width: 100%;
  height: 180px;
  object-fit: cover;
  display: block;
  background: #f0f2f5;
}

.preview-video {
  width: 100%;
  height: 180px;
  display: block;
  background: #000;
  object-fit: contain;
}

.preview-document {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: #f0f2f5;
  color: #111b21;
  text-decoration: none;
  transition: background 0.2s;
  margin: 0;
}

.preview-document:hover {
  background: #e9edef;
}

.preview-document svg {
  flex-shrink: 0;
  color: #00a884;
  width: 20px;
  height: 20px;
}

.preview-document span {
  font-size: 13px;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #111b21;
}

.media-placeholder {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  color: #667781;
  font-size: 48px;
  position: relative;
}

.media-placeholder::after {
  content: 'Enter URL to preview';
  font-size: 12px;
  color: #8696a0;
  position: absolute;
  bottom: 16px;
}

.media-error {
  background: linear-gradient(135deg, #ffebee 0%, #fce4ec 100%);
}

.media-error::after {
  content: 'Invalid or broken URL';
  color: #ef4444;
}

/* Excel Upload Styles */
.excel-phone-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 10px;
  padding: 10px 12px;
  background: #fef9ec;
  border: 1px solid #e67e22;
  border-radius: 6px;
  color: #7d4e00;
  font-size: 13px;
  line-height: 1.5;
}

.excel-phone-warning svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.excel-upload-container {
  margin-bottom: 20px;
}

.upload-excel-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #25D366;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.upload-excel-btn:hover {
  background: #20c05a;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

.upload-excel-btn:disabled {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.file-uploaded-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--bg-tertiary);
  border: 2px solid #25D366;
  border-radius: 8px;
}

.file-name {
  font-weight: 500;
  color: var(--text-primary);
}

.file-rows {
  color: var(--text-secondary);
  font-size: 13px;
}

.clear-file-btn {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: color 0.2s;
  line-height: 1;
}

.clear-file-btn:hover {
  color: #e74c3c;
}

/* Excel Phone Column Highlight Section */
.excel-phone-column-highlight {
  margin: 20px 0;
  padding: 20px;
  background: rgba(37, 211, 102, 0.05);
  border: 2px solid #25D366;
  border-radius: 12px;
}

.highlight-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(37, 211, 102, 0.1);
  border-radius: 8px;
  margin-bottom: 16px;
}

.highlight-banner svg {
  flex-shrink: 0;
}

.highlight-banner span {
  font-weight: 600;
  font-size: 14px;
  color: #25D366;
}

.excel-phone-column-highlight label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
}

.excel-column-select {
  width: 100%;
  padding: 14px;
  border: 2px solid #25D366;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.excel-column-select:focus {
  outline: none;
  border-color: #20c05a;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.excel-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background: var(--bg-secondary);
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 13px;
}

/* Variable Mapping Three-Way Selector */
.mapping-type-selector {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.type-option {
  flex: 1;
  min-width: 120px;
  padding: 10px 12px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: 12px;
  white-space: nowrap;
}

.type-option.active {
  border-color: #25D366;
  background: rgba(37, 211, 102, 0.1);
  color: #25D366;
  font-weight: 600;
}

.type-option input[type="radio"] {
  display: none;
}

.type-option:hover {
  border-color: #25D366;
  background: var(--bg-secondary);
}

.mapping-input-container {
  margin-top: 12px;
}

.full-width {
  width: 100%;
}
</style>
